import React, { MutableRefObject, ReactNode, useState, useEffect, useRef, useCallback, memo, useMemo } from "react";
import classNames from "classnames";
import { MenuContainer } from "./styles.js";
import { usePopper } from "react-popper";
import { detectOverflow, type Modifier, type Placement } from "@popperjs/core";
import { createPortal } from "react-dom";
import { AnimatePresence, motion } from "framer-motion";
import useOnClickOutside from "../../hooks/useOnClickOutside.js";

export interface ActionMenuAction {
  label: string;
  icon?: ReactNode;
  iconStroke?: boolean;
  onClick?: () => void | Record<string, ActionMenuAction> | Promise<void>;
  active?: boolean;
  danger?: boolean;
  destructive?: boolean;
  disabled?: boolean;
  nesting?: number;
}

interface MenuProps {
  currentActions: Record<string, ActionMenuAction>;
  onAction: (action: ActionMenuAction) => void;
  onClose: () => void;
  triggerRef?: React.RefObject<HTMLElement | null>;
}

function Menu({ currentActions, onAction, onClose, triggerRef }: MenuProps) {
  const ref = useRef<HTMLDivElement>(null);
  const innerRef = useRef<HTMLDivElement>(null);
  const actionEntries = Object.entries(currentActions);
  const activeIndex = Math.max(0, actionEntries.findIndex(([, action]) => action.active));
  const [highlightedIndex, setHighlightedIndex] = useState(activeIndex);
  const itemRefs = useRef<(HTMLDivElement | null)[]>([]);
  const outsideClickRefs = useMemo(
    () => (triggerRef ? [ref, triggerRef] : [ref]),
    [triggerRef],
  );

  useOnClickOutside(outsideClickRefs, () => {
    onClose();
  });

  useEffect(() => {
    ref.current?.focus({ preventScroll: true });
  }, []);

  useEffect(() => {
    setHighlightedIndex(activeIndex);
  }, [currentActions, activeIndex]);

  // scrollIntoView would also scroll the page and other ancestors, shifting the card under the menu.
  useEffect(() => {
    const inner = innerRef.current;
    const item = itemRefs.current[highlightedIndex];
    if (!inner || !item) {
      return;
    }
    const innerRect = inner.getBoundingClientRect();
    const itemRect = item.getBoundingClientRect();
    if (itemRect.top < innerRect.top) {
      inner.scrollTop -= innerRect.top - itemRect.top;
    } else if (itemRect.bottom > innerRect.bottom) {
      inner.scrollTop += itemRect.bottom - innerRect.bottom;
    }
  }, [highlightedIndex]);

  const handleBlur = (e: React.FocusEvent) => {
    const relatedTarget = e.relatedTarget as Node | null;
    if (relatedTarget && ref.current?.contains(relatedTarget)) {
      return;
    }
    if (relatedTarget && triggerRef?.current?.contains(relatedTarget)) {
      return;
    }
    onClose();
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    e.stopPropagation();
    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        setHighlightedIndex((prev) => (prev + 1) % actionEntries.length);
        break;
      case "ArrowUp":
        e.preventDefault();
        setHighlightedIndex((prev) => (prev - 1 + actionEntries.length) % actionEntries.length);
        break;
      case "Home":
        e.preventDefault();
        setHighlightedIndex(0);
        break;
      case "End":
        e.preventDefault();
        setHighlightedIndex(actionEntries.length - 1);
        break;
      case "Enter":
      case " ":
        e.preventDefault();
        if (actionEntries[highlightedIndex]) {
          const [, action] = actionEntries[highlightedIndex];
          if (action.onClick) {
            onAction(action);
          }
        }
        break;
      case "Escape":
        e.preventDefault();
        onClose();
        break;
      default:
        break;
    }
  };

  return (
    <MenuContainer ref={ref} role="menu" tabIndex={-1} onKeyDown={handleKeyDown} onBlur={handleBlur}>
      <div ref={innerRef} className="custom-field-action-menu__inner">
        {actionEntries.map(([actionName, action], index) => (
          <div
            ref={(el) => (itemRefs.current[index] = el)}
            role="menuitem"
            tabIndex={-1}
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();

              onAction(action);
            }}
            onMouseEnter={() => setHighlightedIndex(index)}
            key={actionName}
            className={classNames("menu-item", actionName, { active: action.active, disabled: !action.onClick, danger: action.danger, destructive: action.destructive, highlighted: index === highlightedIndex })}
            style={{ paddingLeft: `${12 + (action.nesting || 0) * 24}px` }}
          >
            {action.icon && <div className={classNames("icon", { stroke: action.iconStroke })}>{action.icon}</div>}
            <div className="label">{action.label}</div>
          </div>
        ))}
      </div>
    </MenuContainer>
  );
}

// Must render above `.project-top-bar` when share/settings/help menus are open (z-index: 100002).
const ACTION_MENU_PORTAL_Z_INDEX = 100003;

const VIEWPORT_PADDING = 8;

// Exposes the room left on the placed side so the menu scrolls instead of being clipped by the viewport.
const maxHeightModifier: Modifier<"actionMenuMaxHeight", Record<string, never>> = {
  name: "actionMenuMaxHeight",
  enabled: true,
  phase: "main",
  requiresIfExists: ["offset", "preventOverflow", "flip"],
  fn({ state }) {
    const overflow = detectOverflow(state, { padding: VIEWPORT_PADDING });
    const side = state.placement.startsWith("top") ? "top" : "bottom";
    const available = Math.max(0, state.rects.popper.height - overflow[side]);
    state.styles.popper = { ...state.styles.popper, "--action-menu-max-height": `${Math.floor(available)}px` } as typeof state.styles.popper;
  },
};

const POPPER_MODIFIERS = [
  { name: "computeStyles", options: { gpuAcceleration: false } },
  { name: "flip", options: { padding: VIEWPORT_PADDING } },
  { name: "preventOverflow", options: { padding: VIEWPORT_PADDING } },
  maxHeightModifier,
];

interface ActionMenuPortalProps {
  referenceElement: HTMLDivElement | null;
  visible: boolean;
  position: Placement;
  portalRef?: MutableRefObject<HTMLElement | null>;
  currentActions: Record<string, ActionMenuAction>;
  onClose: () => void;
  onAction: (action: ActionMenuAction) => void;
}

const ActionMenuPortal = memo(function ActionMenuPortal({
  referenceElement,
  visible,
  position,
  portalRef,
  currentActions,
  onClose,
  onAction,
}: ActionMenuPortalProps) {
  const [popperElement, setPopperElement] = useState<HTMLDivElement | null>(null);
  const [positionReady, setPositionReady] = useState(false);
  const triggerElementRef = useRef<HTMLDivElement | null>(null);
  triggerElementRef.current = referenceElement;
  const { styles, attributes, update } = usePopper(referenceElement, popperElement, {
    placement: position,
    modifiers: POPPER_MODIFIERS,
  });

  useEffect(() => {
    if (popperElement && portalRef) {
      portalRef.current = popperElement;
    }
  }, [popperElement, portalRef]);

  useEffect(() => {
    if (!visible) {
      setPositionReady(false);
      return;
    }

    let cancelled = false;
    const showMenu = () => {
      if (!cancelled) {
        setPositionReady(true);
      }
    };

    void Promise.resolve(update?.()).then(() => {
      requestAnimationFrame(showMenu);
    });

    return () => {
      cancelled = true;
    };
  }, [visible, update]);

  // The menu mounts after the first position update, so re-measure once it has real dimensions.
  useEffect(() => {
    if (visible && positionReady) {
      void update?.();
    }
  }, [visible, positionReady, currentActions, update]);

  return createPortal(
    <div
      ref={setPopperElement}
      style={{
        ...styles.popper,
        zIndex: ACTION_MENU_PORTAL_Z_INDEX,
        visibility: visible ? "visible" : "hidden",
        pointerEvents: visible ? "auto" : "none",
      }}
      {...attributes.popper}
    >
      <AnimatePresence>
        {visible && positionReady && (
          <motion.div
            transition={{ duration: 0.15 }}
            initial={{ opacity: 0, y: -30 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -30 }}
          >
            <Menu
              currentActions={currentActions}
              onClose={onClose}
              onAction={onAction}
              triggerRef={triggerElementRef}
            />
          </motion.div>
        )}
      </AnimatePresence>
    </div>,
    document.body,
  );
});

interface ActionMenuProps {
  actions: Record<string, ActionMenuAction>;
  position?: Placement;
  portalRef?: MutableRefObject<HTMLElement | null>;
  children: (openMenu: (event: React.MouseEvent | React.KeyboardEvent) => void) => React.ReactNode;
  visible?: boolean;
  onClose?: () => void;
}

export default function ActionMenu({ actions, position = "bottom", portalRef, children, visible: controlledVisible, onClose }: ActionMenuProps) {
  const [referenceElement, setReferenceElement] = useState<HTMLDivElement | null>(null);
  const [internalVisible, setInternalVisible] = useState(false);
  const isControlled = typeof controlledVisible === "boolean";
  const visible = isControlled ? controlledVisible : internalVisible;
  const triggerRef = useRef<HTMLDivElement | null>(null);

  const [{ actions: currentActions, nested }, setCurrentActions] = useState<{ actions: Record<string, ActionMenuAction>; nested: boolean }>({ actions, nested: false });

  useEffect(() => {
    if (!nested) {
      setCurrentActions({ actions, nested: false });
    }
  }, [actions, nested]);

  const visibleActions = nested ? currentActions : actions;

  const openMenu = useCallback(() => {
    if (!isControlled) {
      setInternalVisible(true);
    }
  }, [isControlled]);

  const triggerOpenMenu = useCallback((event: React.MouseEvent | React.KeyboardEvent) => {
    event.stopPropagation();
    event.preventDefault();

    openMenu();
  }, [openMenu]);

  const closeMenu = useCallback(() => {
    if (!isControlled) {
      setInternalVisible(false);
    }
    onClose?.();
    setCurrentActions({ actions, nested: false });
    requestAnimationFrame(() => {
      const focusTarget = triggerRef.current?.querySelector<HTMLElement>('[tabindex], button, [role="button"]') || triggerRef.current;
      focusTarget?.focus({ preventScroll: true });
    });
  }, [actions, isControlled, onClose]);

  const handleMenuAction = useCallback((action: ActionMenuAction) => {
    if (action.onClick) {
      const newActions = action.onClick();

      if (newActions && !(newActions instanceof Promise)) {
        setCurrentActions({ actions: newActions as Record<string, ActionMenuAction>, nested: true });
      } else {
        closeMenu();
      }
    }
  }, [closeMenu]);

  const previousVisibleRef = useRef(visible);
  useEffect(() => {
    if (isControlled) {
      if (previousVisibleRef.current && !visible) {
        setCurrentActions({ actions, nested: false });
      }
      previousVisibleRef.current = visible;
    }
  }, [visible, isControlled, actions]);

  const setReferenceNode = useCallback((node: HTMLDivElement | null) => {
    setReferenceElement(node);
    triggerRef.current = node;
  }, []);

  return (
    <div className="action-menu">
      <div ref={setReferenceNode}>
        {children(triggerOpenMenu)}
      </div>

      <ActionMenuPortal
        referenceElement={referenceElement}
        visible={visible}
        position={position}
        portalRef={portalRef}
        currentActions={visibleActions}
        onClose={closeMenu}
        onAction={handleMenuAction}
      />
    </div>
  );
}
